import * as React from 'react'

import { useNavigate, useLocation } from 'react-router-dom'

import EmptyState from '@/components/base/emptyState'
import { V1MotiWorkoutsFeed } from '@/lib/api/lib/models/V1MotiWorkoutsFeed'
import { V1MotiActions } from '@/lib/api/lib/models/V1MotiActions'
import { isPremiumUser } from '@/lib/cookies'
import { sendCheer } from '@/lib/cheer'
import { useMotivationStream } from '@/hooks/useMotivationStream'
import { appendReferrer } from '@/lib/referrerState'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'

import UserAvatar from '@/components/base/userAvatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import {
    Sheet as SheetDrawer,
    SheetContent,
    SheetClose
} from '@/components/ui/sheet'

import { MessageCircle, Send, Radio } from 'lucide-react'
import { exerciseLabel } from '@/lib/programme/exerciseLabel'

type ActiveTrainingProps = {
    userId?: string
}

const emos = ['👋', '👍', '👌', '🫡', '🥇', '🔥', '🚀', '🤝', '👏', '🙌', '⚡']

export default function ActiveTraining(props: ActiveTrainingProps) {
    const appContext = React.useContext(AppContext)
    const navigate = useNavigate()
    const location = useLocation()
    const [transition, setTransition] = React.useState('')

    const [workoutsFeed, setWorkoutsFeed] = React.useState<
        V1MotiWorkoutsFeed | undefined
    >(undefined)
    const [emotiSent, setEmotiSent] = React.useState(false)

    const [displayDrawer, setDisplayDrawer] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const [emotiMessage, setEmotiMessage] = React.useState('')

    const current = React.useMemo(
        () => emos[Math.floor(Math.random() * emos.length)],
        [workoutsFeed]
    )

    useMotivationStream<V1MotiWorkoutsFeed>(
        V1MotiActions.Workout,
        (feed) => {
            setTransition('slide-left fadeIn')
            setTimeout(() => {
                setTransition('')
            }, 300)
            setWorkoutsFeed(feed)
            setEmotiSent(false)
            setLoading(false)
        },
        { onOpen: () => setLoading(false) }
    )

    const isScanning = !workoutsFeed

    // Nobody training right now: say so, and offer the one step that puts
    // the user in this feed.
    if (isScanning) {
        return (
            <EmptyState
                variant="section"
                kind="waiting"
                icon={Radio}
                title={appContext.geti18n('empty.live.title')}
                description={appContext.geti18n('empty.live.description')}
                action={{
                    label: appContext.geti18n('empty.live.start'),
                    to: '/schedule/list'
                }}
            />
        )
    }

    return (
        <>
            <div
                className={`${transition} transition-transform duration-300 ease-out rounded-xl border bg-card overflow-hidden relative`}
            >
                <div className="flex items-center gap-2.5 p-3">
                    {isScanning ? (
                        <div className="h-8 w-8 rounded-full bg-rose-500/10 flex items-center justify-center shrink-0">
                            <Radio className="h-4 w-4 text-rose-500 animate-pulse" />
                        </div>
                    ) : (
                        <LinkWithState
                            to={`${workoutsFeed?.userId ? '/coaches/' + workoutsFeed?.userId : '#'}`}
                        >
                            <UserAvatar
                                photo={workoutsFeed?.photo}
                                className="h-8 w-8"
                                fallbackClassName="text-xs"
                            />
                        </LinkWithState>
                    )}
                    <div className="flex-1 min-w-0">
                        <Typography
                            variant="body-sm"
                            className={`truncate font-medium ${isScanning ? 'text-muted-foreground' : 'text-foreground'}`}
                        >
                            {workoutsFeed?.excircise
                                ? exerciseLabel(
                                      { exercise: workoutsFeed.excircise },
                                      appContext.getExercise,
                                      appContext.geti18n
                                  )
                                : appContext.geti18n(
                                      'home.title.livestream.waiting'
                                  ) || 'Waiting for activity...'}
                        </Typography>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            disabled={emotiSent || !workoutsFeed}
                            onClick={() => {
                                if (!isPremiumUser()) {
                                    navigate('/payments/subscription', {
                                        state: appendReferrer(
                                            location.state,
                                            location.pathname
                                        )
                                    })
                                    return
                                }
                                setDisplayDrawer(true)
                            }}
                        >
                            <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={emotiSent || !workoutsFeed}
                            onClick={async () => {
                                setEmotiSent(true)
                                if (!workoutsFeed?.userId) return
                                await sendCheer(workoutsFeed.userId, current)
                            }}
                        >
                            <span className="text-base">
                                {emotiSent
                                    ? '✅'
                                    : workoutsFeed
                                      ? current
                                      : '⏳'}
                            </span>
                        </Button>
                    </div>
                </div>
                {/* Indeterminate scanning bar */}
                <div className="h-[2px] w-full bg-secondary/50 overflow-hidden">
                    {isScanning && (
                        <div
                            className="h-full w-2/5"
                            style={{
                                background:
                                    'linear-gradient(90deg, transparent 0%, oklch(0.65 0.18 15 / 0.8) 50%, transparent 100%)',
                                animation:
                                    'live-scan 2.8s ease-in-out infinite alternate'
                            }}
                        />
                    )}
                </div>
            </div>
            <style>{`
                @keyframes live-scan {
                    0% { transform: translateX(-80%); }
                    100% { transform: translateX(330%); }
                }
            `}</style>
            <SheetDrawer
                open={displayDrawer}
                onOpenChange={(open) => !loading && setDisplayDrawer(open)}
            >
                <SheetContent side="top" className="rounded-b-xl">
                    <SheetClose />
                    <div className="p-4">
                        <div className="space-y-2">
                            <Textarea
                                placeholder={appContext.geti18n(
                                    'requests.messages.placeholder'
                                )}
                                rows={4}
                                name={'message'}
                                value={emotiMessage}
                                onChange={(e) => {
                                    const val = e.target.value.substring(0, 120)
                                    setEmotiMessage(val)
                                }}
                                className="rounded-xl resize-none"
                                required
                            />
                        </div>
                    </div>
                    <div className="px-4 pb-4">
                        <Button
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
                            disabled={loading}
                            onClick={async () => {
                                setLoading(true)

                                if (!workoutsFeed?.userId) {
                                    setLoading(false)
                                    setEmotiSent(true)
                                    setDisplayDrawer(false)
                                    setEmotiMessage('')
                                    return
                                }

                                try {
                                    await sendCheer(
                                        workoutsFeed?.userId || '',
                                        current,
                                        emotiMessage
                                    )
                                } catch (err) {
                                    console.error(err)
                                } finally {
                                    setLoading(false)
                                    setEmotiSent(true)
                                    setDisplayDrawer(false)
                                    setEmotiMessage('')
                                }
                            }}
                        >
                            <Send className="h-4 w-4 mr-1.5" />
                            {appContext.geti18n('training.sendMessage')}
                        </Button>
                    </div>
                </SheetContent>
            </SheetDrawer>
        </>
    )
}
